<template>
  <section class="flex flex-col">
    <div class="flex flex-wrap items-end gap-x-4 gap-y-1 pb-3">
      <div class="flex-1">
        <p class="text-text-primary leading-6 font-medium text-[24px] tracking-[.5px]">{{ $t('play.lan_party.leaderboard') }}</p>
        <p class="text-text-secondary text-sm mt-1">
          {{ period === 'week' ? $t('play.lan_party.leaderboard_week_hint', { n: rows.length }) : $t('play.lan_party.leaderboard_tonight_hint', { n: rows.length }) }}
        </p>
      </div>
      <div class="flex flex-row bg-gray-800 h-8 p-1 rounded-md" role="tablist">
        <button
          v-for="tab in ['tonight', 'week']"
          :key="tab"
          type="button"
          role="tab"
          class="px-3 text-sm rounded-md"
          :class="period === tab ? 'bg-gray-700 text-text-primary' : 'text-text-secondary'"
          @click="period = tab"
        >{{ $t(`play.lan_party.leaderboard_${tab}`) }}</button>
      </div>
    </div>

    <div class="bg-gray-900 h-10 flex items-center text-text-secondary text-xs pl-4 sm:pl-6 pr-4 sm:pr-6 border-b-2 border-gray-950 rounded-t-lg gap-3 sm:gap-6">
      <span class="flex-1">{{ $t('play.lan_party.col_name') }}</span>
      <span class="w-[84px] sm:w-28 text-right">{{ $t('play.lan_party.kda') }}</span>
      <span class="hidden sm:block w-16 text-right">{{ $t('play.lan_party.col_matches') }}</span>
      <span class="w-12 sm:w-14 text-right">{{ $t('play.lan_party.col_points') }}</span>
    </div>
    <div class="flex flex-col gap-[2px] rounded-b-lg overflow-hidden">
      <div v-for="(row, index) in shownRows" :key="row.uuid" :class="rankClass(index, row)">
        <div class="flex items-center bg-gray-900 gap-3 sm:gap-6 pl-4 sm:pl-6 pr-4 sm:pr-6" :class="[index < 3 ? 'h-16' : 'h-14', isMe(row) ? 'user-score-gradient' : '']">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span v-if="index >= 3" class="w-5 sm:w-7 text-sm text-text-secondary tabular-nums flex-shrink-0">{{ index + 1 }}</span>
            <span class="relative flex-shrink-0">
              <LanAvatar :src="row.avatar" size="w-9 h-9 text-sm" />
              <span v-if="index < 3" class="w-5 h-5 rounded-full absolute -bottom-0.5 -right-1 border-2 border-gray-800 text-black text-[10px] font-bold flex items-center justify-center" :class="`rank-${index + 1}-bg`">{{ index + 1 }}</span>
            </span>
            <div class="min-w-0 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
              <span class="text-text-primary font-medium truncate">{{ row.username }}</span>
              <span v-if="isMe(row)" class="text-[11px] px-1.5 py-[1px] rounded whitespace-nowrap" style="background:#4E54C820;color:#8A8EDB">{{ $t('play.lan_party.you') }}</span>
            </div>
          </div>
          <span class="w-[84px] sm:w-28 text-right tabular-nums text-text-primary font-medium text-sm sm:text-base">{{ row.kills }} / {{ row.deaths }} / {{ row.assists }}</span>
          <span class="hidden sm:block w-16 text-right tabular-nums text-text-secondary">{{ row.games }}</span>
          <span class="w-12 sm:w-14 text-right tabular-nums text-text-secondary">{{ row.points }}</span>
        </div>
      </div>
      <p v-if="loaded && rows.length === 0" class="bg-gray-900 px-6 py-5 text-sm text-text-secondary">
        {{ $t('play.lan_party.leaderboard_empty', { min: minGames }) }}
      </p>
    </div>

    <template v-if="me && !shownRows.some((row) => row.uuid === me.uuid)">
      <div class="h-3" />
      <div class="user-rank-border rounded-b-lg overflow-hidden">
        <div class="flex h-14 items-center bg-gray-900 user-score-gradient gap-3 sm:gap-6 pl-4 sm:pl-6 pr-4 sm:pr-6">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <span class="w-5 sm:w-7 text-sm text-text-secondary flex-shrink-0">–</span>
            <LanAvatar :src="me.avatar" size="w-9 h-9 text-sm" />
            <span class="text-text-primary font-medium truncate">{{ me.username }}</span>
            <span class="text-[11px] px-1.5 py-[1px] rounded whitespace-nowrap" style="background:#4E54C820;color:#8A8EDB">{{ $t('play.lan_party.you') }}</span>
            <span v-if="me.games < minGames" class="text-xs text-text-secondary hidden md:inline">{{ $t('play.lan_party.finish_to_get_on_board', { min: minGames }) }}</span>
          </div>
          <span class="w-[84px] sm:w-28 text-right tabular-nums text-text-primary font-medium text-sm sm:text-base">{{ me.games ? `${me.kills} / ${me.deaths} / ${me.assists}` : '–' }}</span>
          <span class="hidden sm:block w-16 text-right tabular-nums text-text-secondary">{{ me.games || '–' }}</span>
          <span class="w-12 sm:w-14 text-right tabular-nums text-text-secondary">{{ me.games ? me.points : '–' }}</span>
        </div>
      </div>
    </template>

    <button
      v-if="rows.length > limit"
      type="button"
      class="self-center mt-4 px-4 py-2.5 rounded-lg bg-gray-800 text-text-primary text-sm font-semibold hover:bg-gray-700"
      @click="limit = rows.length"
    >{{ $t('play.lan_party.see_all', { n: rows.length }) }}</button>
  </section>
</template>

<script setup>
import {
  ref, computed, watch, onBeforeUnmount,
} from 'vue';
import axios from 'axios';
import LanAvatar from './LanAvatar.vue';

const props = defineProps({
  slug: { type: String, required: true },
  creatorName: { type: String, default: '' },
  pollMs: { type: Number, default: 30_000 },
  initialLimit: { type: Number, default: 10 },
});

const emit = defineEmits(['me']);

const period = ref('tonight');
const rows = ref([]);
const me = ref(null);
const minGames = ref(5);
const loaded = ref(false);
const limit = ref(props.initialLimit);
let timer;

const shownRows = computed(() => rows.value.slice(0, limit.value));

function isMe(row) {
  return !!me.value && row.uuid === me.value.uuid;
}

function rankClass(index, row) {
  if (isMe(row)) return 'user-rank-border';
  return index < 3 ? `rank-${index + 1}-border` : '';
}

function load() {
  return axios.get(`/public/creators/${props.slug}/leaderboard`, { params: { period: period.value } })
    .then(({ data }) => {
      rows.value = data.rows;
      me.value = data.me;
      minGames.value = data.min_games;
      loaded.value = true;
      emit('me', { me: data.me, period: period.value });
    })
    .catch(() => {});
}

watch(period, load);
watch(() => props.slug, load);
load();
if (props.pollMs) timer = setInterval(load, props.pollMs);
onBeforeUnmount(() => clearInterval(timer));
</script>

<style scoped>
.rank-1-border { border-left: 3px solid transparent; border-image-source: linear-gradient(180deg, #F7A94C 0%, #F7E64C 100%); border-image-slice: 1; }
.rank-2-border { border-left: 3px solid transparent; border-image-source: linear-gradient(180deg, #959595 0%, #EEEEEE 100%); border-image-slice: 1; }
.rank-3-border { border-left: 3px solid transparent; border-image-source: linear-gradient(180deg, #F68D4F 0%, #FBB083 100%); border-image-slice: 1; }
.user-rank-border { border-left: 3px solid #4E54C8; }
.rank-1-bg { background-image: linear-gradient(180deg, #F7A94C 0%, #F7E64C 100%); }
.rank-2-bg { background-image: linear-gradient(180deg, #959595 0%, #EEEEEE 100%); }
.rank-3-bg { background-image: linear-gradient(180deg, #F68D4F 0%, #FBB083 100%); }
.user-score-gradient { background: linear-gradient(90deg, rgba(78, 84, 200, 0.15) 0%, rgba(78, 84, 200, 0) 100%); }
</style>
